import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import SEO, { screenLd } from "@/components/SEO";
import api from "@/lib/api";
import {
  Tv2, MapPin, ArrowUpRight, ShieldCheck, Clock, ExternalLink, AlertTriangle,
} from "lucide-react";

/**
 * /s/:handle — public screen landing page.
 *
 * Handle shape: ``<country>/<city>/<area>/<venue>/<screen>`` (the part
 * after ``mirrorloops://``). Everything on this page is public catalog
 * data (status_label='listed'). No telemetry is ever shown here — that
 * lives on Work and is operator-only.
 *
 * The "Open in Work" CTA deep-links authenticated operators to the
 * matching screen on work.mirrorloops.com; unauthenticated visitors
 * just see this public summary.
 */

const EVIDENCE_META = {
  observed: { label: "Observed", tone: "border-[#CCFF00]/40 bg-[#CCFF00]/[0.06] text-[#CCFF00]" },
  modelled: { label: "Modelled", tone: "border-[#E8D6A3]/40 bg-[#E8D6A3]/[0.06] text-[#E8D6A3]" },
  inferred: { label: "Inferred", tone: "border-white/15 bg-white/[0.03] text-white/60" },
};

export default function PublicScreen() {
  const { "*": splat } = useParams();
  const handle = splat || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/v1/screens/handle/${handle}`);
        setData(r.data);
      } catch (e) {
        setError(
          e?.response?.status === 404
            ? "Unknown screen. If this is a brand-new install, it may not be listed in the public catalog yet."
            : "Couldn't load this screen.",
        );
      }
    })();
  }, [handle]);

  if (error) {
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-24">
          <div className="border border-red-400/30 bg-red-500/[0.04] rounded p-5 text-red-200">
            <div className="flex items-center gap-2 mb-1 text-sm font-semibold">
              <AlertTriangle size={16} /> {error}
            </div>
            <div className="text-xs text-white/50 mt-2 font-mono break-all">Handle: mirrorloops://{handle}</div>
          </div>
          <Link to="/network" className="inline-block mt-6 text-[#D4B878] underline">
            Browse the public network →
          </Link>
        </div>
      </PublicShell>
    );
  }

  if (!data) {
    return <PublicShell><div className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-white/40">Loading…</div></PublicShell>;
  }

  const s = data.screen || {};
  const ev = EVIDENCE_META[s.evidence] || EVIDENCE_META.inferred;
  const workDeepLink = `https://work.mirrorloops.com/screens/${encodeURIComponent(handle)}`;

  return (
    <PublicShell>
      <SEO
        title={`${s.venue || "Screen"} · ${s.market || "Mirrorloops"}`}
        description={`${s.venue || "A Mirrorloops screen"} in ${s.market || "the network"}. ${s.evidence_note || "Public catalog listing."} Playback is not reach — Mirrorloops publishes catalog data with explicit Observed/Modelled/Inferred provenance.`}
        path={`/s/${handle}`}
        type="place"
        jsonLd={screenLd(s)}
      />
      <div data-testid={`public-screen-${s.handle}`} className="max-w-5xl mx-auto px-6 pt-32 pb-24">
        <div className="text-[11px] text-white/40 mb-4 font-mono">
          <Link to="/network" className="hover:text-white">Network</Link>
          <span className="mx-2">/</span>
          <span className="text-white/70">Screen</span>
        </div>

        <div className="border border-[#D4B878]/25 rounded-lg p-6 mb-8 bg-gradient-to-br from-[#D4B878]/[0.05] to-transparent">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] mb-1 flex items-center gap-2">
                <Tv2 size={11} /> Screen · public listing
                <span
                  data-testid="screen-evidence-badge"
                  className={`ml-1 text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${ev.tone}`}
                >
                  {ev.label}
                </span>
              </div>
              <h1 className="text-3xl font-semibold text-white leading-tight">
                {s.venue || "Mirrorloops screen"}
              </h1>
              <div className="flex gap-4 text-xs text-white/60 mt-3 flex-wrap">
                <span><MapPin size={11} className="inline mr-1" />{s.market}, {s.country}</span>
                {s.resolution && <span className="font-mono">{s.resolution}</span>}
                {s.orientation && (
                  <span className="uppercase tracking-[0.14em] text-[10px] text-white/50">
                    {s.orientation}
                  </span>
                )}
                <span
                  data-testid="screen-status-label"
                  className="uppercase tracking-[0.14em] text-[10px] text-[#E8D6A3]"
                >
                  Status · {s.status_label}
                </span>
              </div>
            </div>
            <a
              href={workDeepLink}
              target="_blank"
              rel="noreferrer"
              data-testid="screen-work-deep-link"
              className="rounded-full bg-[#D4B878] hover:bg-[#E8D6A3] text-black font-semibold text-xs px-4 py-2 flex items-center gap-1.5 whitespace-nowrap"
            >
              <ShieldCheck size={12} /> Open in Work <ExternalLink size={11} />
            </a>
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 text-[11px] text-white/40 font-mono break-all">
            <span className="text-white/60">source_ref</span> · {s.source_ref}
          </div>
        </div>

        <div className="border border-white/10 rounded-lg p-5 bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/40 mb-2 flex items-center gap-1">
            <Clock size={10} /> Provenance
          </div>
          <div className="text-sm text-white/70 leading-relaxed">
            {s.evidence_note || "Catalog listing — not live telemetry."}{" "}
            Playback is not reach. Live measurement for this screen lives on the operator side
            (Work) and requires an authorised operator session.
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-[11px] text-white/50 font-mono">
            <div>
              <div className="uppercase tracking-[0.14em] text-white/40 mb-0.5">Venue type</div>
              <div className="text-white/80">{s.venue_type || "—"}</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.14em] text-white/40 mb-0.5">Updated</div>
              <div className="text-white/80">{String(s.updated_at || "").slice(0, 10)}</div>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-3">
          <Link
            to="/network"
            className="rounded-full border border-white/15 text-white hover:bg-white/5 px-5 py-2 text-sm text-center"
          >
            ← All screens in {s.market}
          </Link>
          <Link
            to="/register"
            className="rounded-full bg-[#CCFF00] hover:bg-[#B8E600] text-black font-semibold px-5 py-2 text-sm text-center flex items-center gap-1.5 justify-center"
          >
            Advertise on this screen <ArrowUpRight size={12} />
          </Link>
        </div>
      </div>
    </PublicShell>
  );
}
